/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, FunctionComponent } from "react";
import Select, { OnChangeValue } from "react-select";
import {
  Draggable,
  ConfirmModal,
  ConditionProps,
  DEFAULT_TIMEZONE,
  OptionTypeBase,
  REPORTING_PRIORITY,
  PATIENT_CLASS,
} from "@advapacs/common";
import { useTranslation } from "react-i18next";
import {
  DAY_OF_WEEK,
  DICOM,
  DYNAMIC,
  HL7,
  MODALITY,
  GENDER,
  BODY_PART,
  EXAM_CODE,
  AGE_RANGE,
  STUDY_DESCRIPTION,
  ORGANISATION,
  SCRIPT,
  SITE,
  TIME_OF_DAY,
  getSelectStyles,
} from "../../constant";
import HL7ServiceField from "./Fields/HL7ServiceField";
import Script from "./Fields/Script";
import Day from "./Fields/Day";
import Time from "./Fields/Time";
import Dynamic from "./Fields/Dynamic";
import Dicom from "./Fields/Dicom";
import Organisation from "./Fields/Organisation";
import Site from "./Fields/Site";
import Modality from "./Fields/Modality";
import ReportingPriority from "./Fields/ReportingPriority";
import Gender from "./Fields/Gender";
import BodyPart from "./Fields/BodyPart";
import ExamCode from "./Fields/ExamCode";
import AgeRange from "./Fields/AgeRange";
import StudyDescription from "./Fields/StudyDescription";
import PatientClass from "./Fields/PatientClass";

const Condition: FunctionComponent<ConditionProps> = ({
  ruleTypes,
  disabled,
  parentId,
  onRemoveCondition,
  id,
  errors,
  condition,
  type,
  onValueChangeHandler,
  isDark,
  organisationOptions,
  siteOptions,
  modalityOptions,
  patientClassOptions,
  showModalityOperator,
  reportPriorityOptions,
  genderOptions,
  bodyPartOptions,
  examCodeOptions,
  onHandleScriptModal,
}: ConditionProps) => {
  const { t } = useTranslation(["rule"]);
  const [canDrag, setCanDrag] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [localType, setLocalType] = useState(type);

  // Event Handlers for Condition
  const deleteRule = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDeleting(true);
  };

  const updateType = (newType: OnChangeValue<OptionTypeBase, false>) => {
    if (newType) {
      setLocalType(newType.value);
      onValueChangeHandler(
        {
          type: newType.value,
          scope: "conditions",
          id,
        },
        parentId
      );
    }
  };

  const getTypeOptions = () => {
    const result = ruleTypes.map((option) => {
      return {
        label: t(option.replaceAll("_", " ").toLowerCase()).toUpperCase(),
        value: option,
      };
    });
    return result;
  };

  const confirmRemove = () => {
    setDeleting(false);
    if (parentId && onRemoveCondition) {
      onRemoveCondition(id, parentId);
    }
  };

  const cancelRemove = () => {
    setDeleting(false);
  };

  return (
    <>
      <Draggable
        className="app-sla-block-container drag-element"
        disableDrag={!canDrag || disabled || parentId === null}
        dataItem={JSON.stringify({ value: condition, parentId })}
      >
        {}
        <ConfirmModal
          show={deleting}
          body={t("confirm_condition_removal")}
          onClose={cancelRemove}
          cancel={cancelRemove}
          confirm={confirmRemove}
        />
        <div className="condition">
          <div className="row p-2 ml-0 mr-0 mt-1 mb-0">
            <div className="col-md-3 col-sm-5 mb-1">
              <div className="d-inline-block" style={{ width: "100%" }}>
                <Select
                  menuPlacement="auto"
                  menuPosition="fixed"
                  id={`${id}_type`}
                  styles={getSelectStyles(isDark)}
                  classNamePrefix={`${isDark ? "dark" : "light"}`}
                  isDisabled={disabled}
                  options={getTypeOptions()}
                  value={getTypeOptions().find(
                    (option) => option?.value === condition?.type
                  )}
                  placeholder={t("select_type")}
                  onChange={updateType}
                />
                {errors.find((item) => item.id === id) !== undefined &&
                  !condition?.type && (
                    <label
                      className="validation-invalid-label"
                      htmlFor={`${id}_type`}
                    >
                      {t("field_required")}
                    </label>
                  )}
              </div>
            </div>

            <div className="col-md-8 col-sm-5">
              <div>
                {localType === ORGANISATION && (
                  <Organisation
                    isDark={isDark}
                    disabled={disabled}
                    options={organisationOptions}
                    errors={errors}
                    id={id}
                    parent={parentId}
                    organisationIds={
                      condition?.stringCollectionProperty?.values
                    }
                    onValueChangeHandler={onValueChangeHandler}
                  />
                )}

                {localType === SITE && (
                  <Site
                    isDark={isDark}
                    disabled={disabled}
                    options={siteOptions}
                    errors={errors}
                    id={id}
                    parent={parentId}
                    siteIds={condition?.stringCollectionProperty?.values}
                    onValueChangeHandler={onValueChangeHandler}
                  />
                )}

                {localType === MODALITY && (
                  <Modality
                    isDark={isDark}
                    disabled={disabled}
                    options={modalityOptions}
                    showOperator={showModalityOperator}
                    errors={errors}
                    id={id}
                    parent={parentId}
                    modality={condition?.stringCollectionProperty?.values}
                    operator={condition?.stringCollectionProperty?.operator}
                    onValueChangeHandler={onValueChangeHandler}
                  />
                )}

                {localType === PATIENT_CLASS && (
                  <PatientClass
                    isDark={isDark}
                    disabled={disabled}
                    options={patientClassOptions}
                    errors={errors}
                    id={id}
                    parent={parentId}
                    patientClass={condition?.stringCollectionProperty?.values}
                    onValueChangeHandler={onValueChangeHandler}
                  />
                )}

                {localType === REPORTING_PRIORITY && (
                  <ReportingPriority
                    isDark={isDark}
                    disabled={disabled}
                    options={reportPriorityOptions}
                    errors={errors}
                    id={id}
                    parent={parentId}
                    priorities={condition?.stringCollectionProperty?.values}
                    onValueChangeHandler={onValueChangeHandler}
                  />
                )}

                {localType === GENDER && (
                  <Gender
                    isDark={isDark}
                    disabled={disabled}
                    options={genderOptions}
                    operator={condition?.stringCollectionProperty?.operator}
                    errors={errors}
                    id={id}
                    parent={parentId}
                    gender={condition?.stringCollectionProperty?.values}
                    onValueChangeHandler={onValueChangeHandler}
                  />
                )}

                {localType === BODY_PART && (
                  <BodyPart
                    isDark={isDark}
                    disabled={disabled}
                    options={bodyPartOptions}
                    operator={condition?.stringCollectionProperty?.operator}
                    errors={errors}
                    id={id}
                    parent={parentId}
                    bodyPart={condition?.stringCollectionProperty?.values}
                    onValueChangeHandler={onValueChangeHandler}
                  />
                )}

                {localType === EXAM_CODE && (
                  <ExamCode
                    isDark={isDark}
                    disabled={disabled}
                    options={examCodeOptions}
                    operator={condition?.stringCollectionProperty?.operator}
                    errors={errors}
                    id={id}
                    parent={parentId}
                    codes={condition?.stringCollectionProperty?.values}
                    onValueChangeHandler={onValueChangeHandler}
                  />
                )}

                {localType === AGE_RANGE && (
                  <AgeRange
                    isDark={isDark}
                    disabled={disabled}
                    id={id}
                    errors={errors}
                    parent={parentId}
                    condition={{
                      ...condition?.integerRangeProperty,
                      property: "ageRange",
                    }}
                    onValueChangeHandler={onValueChangeHandler}
                  />
                )}

                {localType === STUDY_DESCRIPTION && (
                  <StudyDescription
                    isDark={isDark}
                    disabled={disabled}
                    id={id}
                    errors={errors}
                    parent={parentId}
                    condition={{
                      ...condition?.stringProperty,
                      caseSensitive:
                        condition?.stringProperty?.caseSensitive === undefined
                          ? true
                          : condition?.stringProperty?.caseSensitive,
                    }}
                    onValueChangeHandler={onValueChangeHandler}
                  />
                )}
              </div>
              <div>
                {localType === HL7 && (
                  <HL7ServiceField
                    isDark={isDark}
                    disabled={disabled}
                    errors={errors}
                    id={id}
                    parent={parentId}
                    hl7={condition?.hl7}
                    onValueChangeHandler={onValueChangeHandler}
                  />
                )}
              </div>
              <div>
                {localType === DICOM && (
                  <Dicom
                    isDark={isDark}
                    disabled={disabled}
                    errors={errors}
                    id={id}
                    parent={parentId}
                    condition={{
                      ...condition?.dicom,
                      caseSensitive:
                        condition?.dicom?.caseSensitive === undefined
                          ? true
                          : condition?.dicom?.caseSensitive,
                    }}
                    onValueChangeHandler={onValueChangeHandler}
                  />
                )}
                {localType === DAY_OF_WEEK && (
                  <Day
                    disabled={disabled}
                    isDark={isDark}
                    id={id}
                    errors={errors}
                    parent={parentId}
                    condition={{
                      ...condition?.dayOfWeek,
                      timeZone:
                        condition?.dayOfWeek?.timeZone ||
                        DEFAULT_TIMEZONE.value,
                    }}
                    onValueChangeHandler={onValueChangeHandler}
                  />
                )}
                {localType === TIME_OF_DAY && (
                  <Time
                    isDark={isDark}
                    disabled={disabled}
                    id={id}
                    errors={errors}
                    parent={parentId}
                    condition={{
                      ...condition?.timeOfDay,
                      timeZone:
                        condition?.timeOfDay?.timeZone ||
                        DEFAULT_TIMEZONE.value,
                    }}
                    onValueChangeHandler={onValueChangeHandler}
                  />
                )}
                {localType === SCRIPT && (
                  <Script
                    id={id}
                    isDark={isDark}
                    parent={parentId}
                    errors={errors}
                    condition={condition?.script}
                    disabled={disabled}
                    onValueChangeHandler={onValueChangeHandler}
                    onHandleScriptModal={onHandleScriptModal}
                  />
                )}
                {localType === DYNAMIC && (
                  <Dynamic
                    isDark={isDark}
                    disabled={disabled}
                    errors={errors}
                    id={id}
                    parent={parentId}
                    condition={{
                      ...condition?.dynamic,
                      timeZone:
                        condition?.dynamic?.timeZone || DEFAULT_TIMEZONE.value,
                    }}
                    onValueChangeHandler={onValueChangeHandler}
                  />
                )}
              </div>
            </div>
            <div className="col-md-1 col-sm-2 text-right ">
              {!disabled && (
                <div
                  role="button"
                  tabIndex={0}
                  onMouseOver={() => {
                    setCanDrag(true);
                  }}
                  onFocus={() => {
                    setCanDrag(true);
                  }}
                  onMouseLeave={() => {
                    setCanDrag(false);
                  }}
                  className={`${
                    isDark ? "" : "btn"
                  }  btn-sm drag-group d-inline`}
                  style={{ paddingLeft: "0", paddingRight: "0" }}
                >
                  <i className="icon-move" />
                </div>
              )}
              {parentId && !disabled && (
                <button
                  onClick={deleteRule}
                  type="button"
                  className={`${
                    isDark ? "" : "btn"
                  }  btn-sm d-inline ml-1 text-danger`}
                  style={
                    isDark
                      ? {
                          paddingLeft: "0",
                          paddingRight: "0",
                          background: "#232331",
                          border: 0,
                        }
                      : {
                          paddingLeft: "0",
                          paddingRight: "0",
                        }
                  }
                >
                  <i className="icon-trash" title={t("general.delete")} />
                </button>
              )}
            </div>
          </div>
        </div>
      </Draggable>
    </>
  );
};

export default Condition;
